class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.panel = this.querySelector('.cart-drawer__panel');
    this.loading = this.querySelector('[data-cart-loading]');
    this.checkout = this.querySelector('[data-cart-checkout]');
    this.discountInput = this.querySelector('[data-cart-discount-input]');
    this.discountApply = this.querySelector('[data-cart-discount-apply]');

    this.isUpdating = false;
    this.pendingUpdates = {};
    this.updateTimer = null;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) this.close();
      if (event.target.closest('[data-cart-quantity-change]')) this.onQuantityButton(event);
      if (event.target.closest('[data-cart-remove]')) this.onRemove(event);
      if (event.target.closest('[data-cart-clear]')) this.onClear(event);
      if (event.target.closest('[data-cart-discount-apply]')) this.onDiscountApply(event);
    });

    this.addEventListener('change', (event) => {
      if (event.target.closest('[data-cart-quantity-input]')) this.onQuantityInput(event);
      if (event.target.closest('[data-cart-note]')) this.onNoteChange(event);
    });

    this.addEventListener('input', (event) => {
      if (event.target.closest('[data-cart-discount-input]')) this.onDiscountInput(event);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.hidden) this.close();
    });

    document.querySelectorAll('[data-cart-icon]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    });
  }

  open(refreshing = false) {
    this.hidden = false;
    document.body.classList.add('cart-drawer-open');
    // Force a reflow so the transition runs from the closed state.
    this.offsetHeight;
    this.classList.add('is-open');
    if (!refreshing) this.initRecommendations();
  }

  close() {
    this.classList.remove('is-open');
    const finish = () => {
      this.hidden = true;
      document.body.classList.remove('cart-drawer-open');
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
    } else {
      this.addEventListener('transitionend', finish, { once: true });
    }
  }

  onQuantityButton(event) {
    const button = event.target.closest('[data-cart-quantity-change]');
    const key = button.dataset.key;
    const input = this.querySelector(`[data-cart-quantity-input][data-key="${key}"]`);
    if (!input) return;
    const step = 1;
    const current = parseInt(input.value, 10) || 0;
    const next = button.dataset.action === 'increase' ? current + step : Math.max(0, current - step);
    input.value = next;
    this.scheduleUpdate(key, next);
  }

  onQuantityInput(event) {
    const input = event.target;
    const next = parseInt(input.value, 10) || 0;
    this.scheduleUpdate(input.dataset.key, next);
  }

  onRemove(event) {
    const button = event.target.closest('[data-cart-remove]');
    const key = button.dataset.key;
    const item = this.querySelector(`[data-cart-item][data-key="${key}"]`);

    const remove = () => this.scheduleUpdate(key, 0);

    if (item && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      item.classList.add('is-removing');
      let fallbackId;
      const onEnd = () => {
        clearTimeout(fallbackId);
        remove();
      };
      item.addEventListener('transitionend', onEnd, { once: true });
      fallbackId = setTimeout(remove, 400);
    } else {
      remove();
    }
  }

  onClear(event) {
    event.preventDefault();
    if (this.isUpdating) return;
    this.setLoading(true);

    fetch('/cart/clear.js', { method: 'POST' })
      .then((response) => response.json())
      .then((cart) => {
        this.updateCartCount(cart);
        return this.refresh();
      })
      .finally(() => this.setLoading(false));
  }

  onDiscountInput(event) {
    if (!this.checkout) return;
    const code = encodeURIComponent(event.target.value.trim());
    const base = this.checkout.dataset.baseUrl || this.checkout.getAttribute('href').split('?')[0];
    this.checkout.href = code ? `${base}?discount=${code}` : base;
  }

  onDiscountApply(event) {
    event.preventDefault();
    if (!this.checkout || !this.discountInput) return;
    const code = this.discountInput.value.trim();
    if (!code) return;
    const base = this.checkout.dataset.baseUrl || this.checkout.getAttribute('href').split('?')[0];
    this.checkout.href = `${base}?discount=${encodeURIComponent(code)}`;
    this.checkout.focus();
  }

  onNoteChange(event) {
    const note = event.target.value;
    const form = document.getElementById('cart-drawer-form');
    if (!form) return;

    clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(() => {
      const data = new FormData(form);
      data.set('note', note);
      fetch('/cart/update.js', {
        method: 'POST',
        body: data,
      });
    }, 400);
  }

  scheduleUpdate(key, quantity) {
    this.pendingUpdates[key] = quantity;
    clearTimeout(this.updateTimer);
    this.updateTimer = setTimeout(() => this.processPendingUpdates(), 250);
  }

  processPendingUpdates() {
    if (this.isUpdating || Object.keys(this.pendingUpdates).length === 0) return;

    const updates = {};
    Object.keys(this.pendingUpdates).forEach((key) => {
      updates[key] = this.pendingUpdates[key];
    });
    this.pendingUpdates = {};

    this.updateCart(updates);
  }

  updateCart(updates) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    this.setLoading(true);

    const body = new URLSearchParams();
    if (this.sectionId) body.set('sections', this.sectionId);
    Object.entries(updates).forEach(([key, quantity]) => {
      body.set(`updates[${key}]`, quantity);
    });

    fetch('/cart/update.js', { method: 'POST', body })
      .then((response) => response.json())
      .then((data) => {
        if (data.item_count !== undefined) this.updateCartCount(data);
        if (data.sections && data.sections[this.sectionId]) {
          this.replaceSection(data.sections[this.sectionId]);
        } else {
          return this.refresh();
        }
      })
      .catch(() => {})
      .finally(() => {
        this.isUpdating = false;
        this.setLoading(false);
        this.processPendingUpdates();
      });
  }

  replaceSection(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newDrawer = parsed.querySelector('cart-drawer');
    if (newDrawer) {
      this.innerHTML = newDrawer.innerHTML;
      this.panel = this.querySelector('.cart-drawer__panel');
      this.loading = this.querySelector('[data-cart-loading]');
      this.checkout = this.querySelector('[data-cart-checkout]');
      this.discountInput = this.querySelector('[data-cart-discount-input]');
      this.discountApply = this.querySelector('[data-cart-discount-apply]');
      this.initRecommendations();
    }
  }

  setLoading(active) {
    if (this.loading) this.loading.hidden = !active;
    if (this.panel) this.panel.classList.toggle('is-loading', active);
  }

  updateCartCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
      el.classList.remove('is-pulse');
      void el.offsetWidth;
      el.classList.add('is-pulse');
      setTimeout(() => el.classList.remove('is-pulse'), 500);
    });
  }

  refresh() {
    this.isUpdating = true;
    this.setLoading(true);

    const url = new URL(window.location.href);
    url.searchParams.set('sections', this.sectionId);
    return fetch(url.toString())
      .then((response) => response.json())
      .then((data) => {
        const html = data[this.sectionId];
        if (html) {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const newDrawer = parsed.querySelector('cart-drawer');
          if (newDrawer) {
            this.innerHTML = newDrawer.innerHTML;
            // Re-select key references after re-render.
            this.panel = this.querySelector('.cart-drawer__panel');
            this.loading = this.querySelector('[data-cart-loading]');
            this.checkout = this.querySelector('[data-cart-checkout]');
            this.discountInput = this.querySelector('[data-cart-discount-input]');
            this.discountApply = this.querySelector('[data-cart-discount-apply]');
            this.initRecommendations();
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        this.isUpdating = false;
        this.setLoading(false);
      });
  }

  initRecommendations() {
    const container = this.querySelector('[data-cart-recommendations]');
    const grid = this.querySelector('[data-cart-recommendations-grid]');
    if (!container || !grid) return;

    const productIds = (container.dataset.productIds || '')
      .replace(/^\[|\]$/g, '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    if (productIds.length === 0) return;

    const limit = container.dataset.limit || 4;
    const intent = container.dataset.intent || 'related';
    const productId = productIds[0];
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    const url = `${root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const products = (data.products || []).filter(
          (product) => !productIds.includes(String(product.id))
        );
        if (products.length > 0) {
          grid.innerHTML = products.slice(0, limit).map((product) => this.renderRecommendation(product)).join('');
          grid.hidden = false;
        } else {
          this.showRecommendationsFallback(container);
        }
      })
      .catch(() => this.showRecommendationsFallback(container));
  }

  showRecommendationsFallback(container) {
    const fallback = container.querySelector('[data-cart-recommendations-fallback]');
    if (fallback) fallback.hidden = false;
  }

  renderRecommendation(product) {
    const variant = (product.variants || []).find((v) => v.available) || product.variants[0];
    if (!variant) return '';

    const image = product.featured_image || (product.images || [])[0] || '';
    const title = this.escapeHtml(product.title);
    const price = this.formatMoney(variant.price);
    const compare = variant.compare_at_price ? `<s class="product-card__compare-price">${this.formatMoney(variant.compare_at_price)}</s>` : '';
    const addedText = window.cartStrings.addedText;

    return `
      <li class="product-grid__item product-card product-card--compact" data-product-id="${product.id}">
        <a href="${product.url}" class="product-card__media">
          ${image ? `<img src="${image}" alt="${title}" loading="lazy" width="160" height="160">` : ''}
        </a>
        <div class="product-card__info">
          <a href="${product.url}" class="product-card__title">${title}</a>
          <div class="product-card__price">
            <span class="product-card__price-current">${price}</span>
            ${compare}
          </div>
          <button
            type="button"
            class="product-card__quick-add"
            data-quick-add
            data-variant-id="${variant.id}"
            data-added-text="${addedText}"
          >
            ${window.cartStrings.addToCart}
          </button>
        </div>
      </li>
    `;
  }

  formatMoney(cents) {
    if (window.formatMoney) return window.formatMoney(cents);
    return new Intl.NumberFormat(document.documentElement.lang || 'nl', { style: 'currency', currency: Shopify.currency.active || 'USD' }).format(cents / 100);
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('cart-drawer', CartDrawer);
